/** Os hubs da plataforma, desde 28/10/2026: o HubTransGrãos, da transportadora, e o
 *  HubVerticalFarm, das fazendas do grupo. Mesma aplicação, mesmo servidor e mesmo
 *  login: o hub agrupa áreas e dá a identidade visual.
 *
 *  CADA HUB TEM A PRÓPRIA SESSÃO, desde 03/11/2026, pedido do Amorim: entrar no
 *  HubVerticalFarm pede o login de lá, mesmo com a sessão do HubTransGrãos aberta, e
 *  sair de um não derruba o outro. O hub da sessão vai no token, e as rotas da Farm
 *  recusam a do outro hub. Não há botão de troca: o hub sai do endereço, e a tela
 *  de login escolhe onde entrar.
 *
 *  O hub é DA ABA. Nasce do endereço: `/farm` é a Farm, qualquer outra rota de área
 *  é o HubTransGrãos. Rota neutra, como `/login`, `/conta` e `/`, fica com o hub que
 *  a aba já tinha, guardado em `sessionStorage` para sobreviver ao recarregar; aba
 *  nova parte do último escolhido, em `localStorage`, e sem nada, o HubTransGrãos.
 *  É o que mantém a equipe da TransGrãos entrando direto, sem tocar em nada. */
import { useSyncExternalStore } from 'react'

export type HubKey = 'transgraos' | 'farm'

export interface HubDef {
  key: HubKey
  /** Nome por extenso: seletor, logo e mensagens. */
  nome: string
  /** Sigla do título da aba. */
  sigla: string
}

export const HUBS: readonly HubDef[] = [
  { key: 'transgraos', nome: 'HubTransGrãos', sigla: 'HTG' },
  { key: 'farm', nome: 'HubVerticalFarm', sigla: 'HVF' },
]

/** Sem escolha guardada, a entrada abre no HubTransGrãos. */
export const HUB_PADRAO: HubKey = 'transgraos'

/** O último hub escolhido no navegador: o ponto de partida de uma aba nova. A
 *  mesma chave que o `index.html` lê. */
export const HUB_STORAGE_KEY = 'transgraos.hub'
/** O hub desta aba, para recarregar sem mudar de sessão. */
export const HUB_DA_ABA_KEY = 'transgraos.hub.aba'

export function ehHub(valor: unknown): valor is HubKey {
  return HUBS.some((hub) => hub.key === valor)
}

export function hubDef(key: HubKey): HubDef {
  return HUBS.find((hub) => hub.key === key) ?? HUBS[0]
}

/** Rotas de nenhum hub: a entrada, a conta e o piso. Nelas a aba segue no hub que
 *  tinha. Espelho das rotas fora das áreas em `core/router/AppRoutes.tsx`. */
const ROTAS_NEUTRAS = ['/login', '/esqueci-senha', '/primeiro-acesso', '/conta', '/sem-acesso']

/** O hub que o endereço impõe, ou nulo na rota neutra. Sem importar o catálogo de
 *  áreas: este módulo vive embaixo do guarda de tokens. */
export function hubDoEndereco(pathname: string): HubKey | null {
  if (pathname === '/farm' || pathname.startsWith('/farm/')) return 'farm'
  if (pathname === '/' || pathname === '') return null
  if (ROTAS_NEUTRAS.some((r) => pathname === r || pathname.startsWith(`${r}/`))) return null
  return 'transgraos'
}

function lerArmazenado(armazem: () => Storage, chave: string): HubKey | null {
  try {
    const valor = armazem().getItem(chave)
    return ehHub(valor) ? valor : null
  } catch {
    return null
  }
}

function gravar(armazem: () => Storage, chave: string, hub: HubKey): void {
  try {
    armazem().setItem(chave, hub)
  } catch {
    /* armazenamento indisponível: vale até recarregar */
  }
}

function hubInicial(): HubKey {
  if (typeof window === 'undefined') return HUB_PADRAO
  const doEndereco = hubDoEndereco(window.location.pathname)
  if (doEndereco) return doEndereco
  const pedido = new URLSearchParams(window.location.search).get('hub')
  if (ehHub(pedido)) return pedido
  return lerArmazenado(() => sessionStorage, HUB_DA_ABA_KEY)
    ?? lerArmazenado(() => localStorage, HUB_STORAGE_KEY)
    ?? HUB_PADRAO
}

let hubDaAba: HubKey = hubInicial()

/** O hub desta aba: o da sessão em uso, da pele e do título. */
export function hubLembrado(): HubKey {
  return hubDaAba
}

const ouvintes = new Set<() => void>()

/** Põe a aba num hub e o guarda como o ponto de partida das próximas abas. Trocar o
 *  hub troca a sessão em uso: o `AuthProvider` ouve e entra na do hub novo, ou
 *  devolve ao login dele. */
export function lembrarHub(hub: HubKey): void {
  gravar(() => localStorage, HUB_STORAGE_KEY, hub)
  gravar(() => sessionStorage, HUB_DA_ABA_KEY, hub)
  if (typeof document !== 'undefined') document.documentElement.setAttribute('data-hub', hub)
  if (hub === hubDaAba) return
  hubDaAba = hub
  ouvintes.forEach((avisar) => avisar())
}

function assinar(avisar: () => void): () => void {
  ouvintes.add(avisar)
  return () => {
    ouvintes.delete(avisar)
  }
}

/** O hub da aba, atualizado na hora em que o seletor ou o endereço o trocam. */
export function useHubLembrado(): HubKey {
  return useSyncExternalStore(assinar, hubLembrado, () => HUB_PADRAO)
}

/** Para os testes: a aba volta ao estado de quem acabou de abrir. */
export function reiniciarHubDaAba(): void {
  hubDaAba = hubInicial()
  ouvintes.forEach((avisar) => avisar())
}
